/**
 * Pareto brand style configuration — colors, typography, and prompt rules
 * extracted from the client's "Pareto ER Slide Examples.pptx" (slide master
 * colorScheme "PSEC2112" + fontScheme "PSEC", layout placeholder geometry,
 * and real template charts, via the OOXML).
 *
 * Mirrors the shape of `berenbergStyleConfig.ts` / `stjStyleConfig.ts`.
 * When Pareto style is active the prompt below is appended to the system
 * prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: theme colors come from the master-linked colorScheme
 * "PSEC2112". The fontScheme names Cambria (major) and Arial Nova (minor);
 * master txStyles use +mj-lt / +mn-lt, so THEME_COLORS follow those faces
 * rather than the Calibri that appears in sample pitch content. Slide size
 * is 16:9 widescreen (960×540 pt). Title/content geometry is from the
 * "Text" layout. A small wordmark lives on slide master 1 (bottom-right),
 * but that master's first layout is Blank, so content slides must use
 * contentLayoutName "Text" to inherit the branded body layout rather than
 * an empty canvas. Template charts lead with navy (theme accent1 / #003255).
 * Catalog matching and AddSlide trim layout names, so the registry/prompt
 * uses the trimmed form of OOXML cSld names (e.g. "Title slide - blue ").
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "PSEC2112" colorScheme
// ---------------------------------------------------------------------------

export const PARETO_COLORS = {
  navy: "003255", // dk2 / accent1 — Pareto signature navy (primary brand)
  lightBlue: "81B0C0", // accent2
  green: "407061", // accent3
  gray: "8F9DA6", // accent4
  gold: "C9BE27", // accent5
  magenta: "DE0080", // accent6

  ink: "000000", // dk1 / lt2 — titles & body text (black)
  white: "FFFFFF", // lt1

  hyperlink: "738296", // hlink
  followedHyperlink: "C0C0C0", // folHlink

  ruleGray: "8F9DA6", // accent4 — rules / separators
  altRow: "E6EEF2", // light navy-tint for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Pareto style is active. Color slots
 * map 1:1 to the PSEC2112 colorScheme. Fonts follow the PSEC fontScheme
 * (Cambria titles, Arial Nova body), not sample-slide Calibri.
 */
export const PARETO_THEME_COLORS: ThemeColors = {
  dark1: PARETO_COLORS.ink, // #000000 — Text1
  dark2: PARETO_COLORS.navy, // #003255 — Text2
  light1: PARETO_COLORS.white, // #FFFFFF — Background1
  light2: PARETO_COLORS.ink, // #000000 — Background2 (scheme lt2 is black)
  accent1: PARETO_COLORS.navy, // #003255
  accent2: PARETO_COLORS.lightBlue, // #81B0C0
  accent3: PARETO_COLORS.green, // #407061
  accent4: PARETO_COLORS.gray, // #8F9DA6
  accent5: PARETO_COLORS.gold, // #C9BE27
  accent6: PARETO_COLORS.magenta, // #DE0080
  hyperlink: PARETO_COLORS.hyperlink,
  followedHyperlink: PARETO_COLORS.followedHyperlink,
  majorFont: "Cambria",
  minorFont: "Arial Nova",
};

/**
 * Ordered series palette. Template charts use theme accents, led by navy
 * (accent1 / dk2), then light blue, green, gold, gray, magenta.
 */
export const PARETO_CHART_SERIES_PALETTE = [
  PARETO_COLORS.navy,
  PARETO_COLORS.lightBlue,
  PARETO_COLORS.green,
  PARETO_COLORS.gold,
  PARETO_COLORS.gray,
  PARETO_COLORS.magenta,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from master + "Text" layout)
// ---------------------------------------------------------------------------

export const PARETO_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const PARETO_POSITIONS = {
  title: { left: 33.8, top: 37.6, width: 892.4, height: 31.9 },
  contentStart: { left: 33.8, top: 105.6 },
  footnote: { left: 34.2, top: 506.6, width: 774.6, height: 27.5 },
} as const;

// Authoritative layout names from the Pareto master. Catalog matching and
// AddSlide trim, so names here match the trimmed catalog rather than the raw
// OOXML cSld/@name (e.g. "Title slide - blue " stores a trailing space).
export const PARETO_LAYOUT_NAMES = [
  "Blank",
  "Title slide - blue",
  "Title slide - full picture (background)",
  "Title slide - small picture",
  "Company presentation - blue background",
  "Company presentation - full picture background",
  "Table of contents",
  "Full picture slide",
  "Chapter slide - dark blue",
  "Chapter slide - light blue",
  "Chapter slide - green",
  "Chapter slide - gray",
  "Quote",
  "2 objects",
  "4 objects",
  "6 objects",
  "8 objects",
  "3 objects",
  "4 narrow objects",
  "1+4 objects",
  "4+1 objects",
  "1+1 objects",
  "1 object",
  "Text",
  "No objects",
  "Standard dislaimer",
  "Contact information",
  "Buy",
  "1_1+1 objects",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Pareto style is active
// ---------------------------------------------------------------------------

export const PARETO_STYLE_PROMPT = `

## PARETO BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Pareto.pptx" contains the current Pareto visual standard. Match the closest relevant precedent for covers, one-/two-object content, charts, and notes/sources footers. Prefer its title/subtitle/kicker hierarchy, bottom-right wordmark, navy-led charts, and gray tables over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Pareto uses:
- Titles / headings: \`"Cambria"\`
- Body, bullets, tables, charts, notes, kickers: \`"Arial Nova"\`
NEVER omit fontFamily when Pareto style is active. If a shape already has the wrong font (including Calibri from sample content), include fontFamily to correct it.

### Color palette (from the Pareto master colorScheme "PSEC2112")
Use ONLY these Pareto brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${PARETO_COLORS.ink}
- Text2 / dark2: Navy #${PARETO_COLORS.navy}
- Background1 / light1: White #${PARETO_COLORS.white}
- Background2 / light2: Black #${PARETO_COLORS.ink} (scheme lt2 is black — do not use as a slide fill)
- Accent1: Navy #${PARETO_COLORS.navy}
- Accent2: Light blue #${PARETO_COLORS.lightBlue}
- Accent3: Green #${PARETO_COLORS.green}
- Accent4: Gray #${PARETO_COLORS.gray}
- Accent5: Gold #${PARETO_COLORS.gold}
- Accent6: Magenta #${PARETO_COLORS.magenta}

**Usage:**
- **Primary brand / emphasis**: Navy #${PARETO_COLORS.navy}
- **Secondary emphasis**: Light blue #${PARETO_COLORS.lightBlue}
- **Tertiary / highlight**: Green #${PARETO_COLORS.green} or gold #${PARETO_COLORS.gold}
- **Text / ink**: Black #${PARETO_COLORS.ink}
- **Lines & borders**: Gray #${PARETO_COLORS.ruleGray}
- **Background / shading**: Light navy-tint #${PARETO_COLORS.altRow}
- **Chart series palette** (in order): ${PARETO_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${PARETO_COLORS.navy}) as the first/primary series color.
- Magenta (#${PARETO_COLORS.magenta}) is a scheme accent — use sparingly, not as a default fill.

### Typography (authoritative — from the Pareto master / "Text" layout)
Slide dimensions: 960 × 540 pt (16:9).

**Placeholder positions:**
- **Kicker**: left=34.1, top=14.2, width=429.1, height=16.1 — "Arial Nova" 8pt
- **Title**: left=33.8, top=37.6, width=892.4, height=31.9 — "Cambria" 26pt, black (#${PARETO_COLORS.ink}) or navy, left-aligned
- **Subtitle**: left=33.7, top=71.5, width=892.8, height=17.2 — "Arial Nova" 14pt
- **Content area**: starts at left=33.8, top=105.6, width=892.4, height=396.7
- **Notes / sources**: left=34.2, top=506.6, width=774.6, height=27.5 — "Arial Nova" 7pt
- **Wordmark**: bottom-right around left=827, top=511 (master picture — do not recreate)

**Content text hierarchy ("Arial Nova", black #${PARETO_COLORS.ink}, ~12pt body):**
- Keep body text ~10-12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use Cambria or the navy accent; bullets otherwise inherit black text.

### Charts (Pareto style)
- Font: "Arial Nova", minimum 8pt
- **Primary data color**: navy (#${PARETO_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Pareto style)
- Header row: navy (#${PARETO_COLORS.navy}) fill with white text, bold
- Body: white fill, black "Arial Nova" text (10pt)
- Alternate-row shading: light navy-tint (#${PARETO_COLORS.altRow})
- Borders: thin gray (#${PARETO_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Pareto style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${PARETO_COLORS.ruleGray}) or navy (#${PARETO_COLORS.navy}) rule lines for separation when needed
- Connectors: solid gray (#${PARETO_COLORS.ruleGray}) lines
- Page numbers / notes: 7-8pt, "Arial Nova"
- Prefer layout "Text" for narrative body slides. Use "1 object" for a single chart/table and "1+1 objects" / "2 objects" for multi-panel pages. Do not default to "Blank".
- Cover / section pages: "Title slide - blue", "Chapter slide - dark blue" (and the light-blue / green / gray chapter variants).

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
